import React, { useState } from 'react';
import WorkflowButton from './WorkflowButton';
import WorkflowList from './WorkflowList';
import AddressFormModal from './AddressFormModal';
import LocationCapture from './LocationCapture';
import MediaUpload from './MediaUpload';
import { MapPinHouse } from 'lucide-react';

interface WorkflowMessageType {
  id: string;
  type: 'workflow';
  messageType: 'button' | 'list' | 'media_button' | 'question' | 'media_question' | 'text' | 'ask_address' | 'ask_location';
  text?: string;
  buttons?: { id: string; label: string; nextNodeId?: string }[];
  header?: string;
  body?: string;
  footer?: string;
  buttonText?: string;
  sections?: { title: string; items: { id: string; title: string; description?: string; nextNodeId?: string }[] }[];
  mediaType?: string;
  mediaUrl?: string;
  caption?: string;
  sessionId?: string;
  contextMessageId?: string; // For threading/replying to specific messages
}

interface WorkflowMessageProps {
  message: WorkflowMessageType;
  onButtonClick?: (buttonId: string, buttonLabel?: string, contextMessageId?: string) => void;
  onListItemSelect?: (itemId: string, itemTitle?: string, contextMessageId?: string) => void;
  onAddressSubmit?: (address: string) => void;
  onLocationCapture?: (latitude: number, longitude: number) => void;
  onMediaUpload?: (file: File) => Promise<void>;
  disabled?: boolean;
  className?: string;
  primaryColor?: string;
  containedModal?: boolean; // New prop to control modal positioning
}

const WorkflowMessage: React.FC<WorkflowMessageProps> = ({
  message,
  onButtonClick,
  onListItemSelect,
  onAddressSubmit,
  onLocationCapture,
  onMediaUpload,
  disabled = false,
  className = '',
  primaryColor,
  containedModal = false
}) => {

  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const renderMediaContent = () => {
    if (message.mediaUrl && message.mediaType) {
      if (message.mediaType.startsWith('image/') || message.mediaType === 'image') {
        return (
          <div className="relative p-3">
            <img
              src={message.mediaUrl}
              alt={message.caption || 'Media'}
              className="w-full h-auto rounded-sm object-contain block"
            />
          </div>
        );
      } else if (message.mediaType.startsWith('video/') || message.mediaType === 'video') {
        return (
          <div className="relative p-3">
            <video
              src={message.mediaUrl}
              controls
              className="max-w-full h-auto rounded-sm"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
      } else {
        return (
          <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-sm mb-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <a
              href={message.mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
            >
              <span>{message.caption || 'Download File'}</span>
            </a>
          </div>
        );
      }
    }
    return null;
  };

  const renderContent = () => {
    switch (message.messageType) {
      case 'button':
      case 'media_button':
        return (
          <div>
            {renderMediaContent()}
            {message.text && (
              <div className="p-3 text-gray-800 dark:text-white text-sm bg-white dark:bg-gray-700/60 whitespace-pre-wrap">
                {message.text}
              </div>
            )}
            {message.caption && !message.text && (
              <div className="p-3 text-gray-800 dark:text-white text-sm break-all px-3 whitespace-pre-wrap">
                {message.caption.includes('http') ? (
                  <a href={message.caption} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                    {message.caption}
                  </a>
                ) : (
                  message.caption
                )}
              </div>
            )}
            {message.buttons && message.buttons.length > 0 && (
              <WorkflowButton
                buttons={message.buttons.map(btn => ({...btn, contextMessageId: message.contextMessageId}))}
                onButtonClick={onButtonClick}
                disabled={disabled}
              />
            )}
          </div>
        );

      case 'list':
        return (
          <div>
            {message.sections && message.sections.length > 0 && (
              <WorkflowList
                header={message.header}
                body={message.body}
                footer={message.footer}
                buttonText={message.buttonText}
                sections={message.sections.map(section => ({
                  ...section,
                  items: section.items.map(item => ({...item, contextMessageId: message.contextMessageId}))
                }))}
                onItemSelect={onListItemSelect}
                disabled={disabled}
                containedModal={containedModal}
              />
            )}
          </div>
        );

      case 'question':
        return (
          <div>
            {message.text && (
              <div className="text-gray-800 dark:text-white p-3">
                {message.text}
              </div>
            )}
          </div>
        );

      case 'media_question':
        return (
          <div className='px-3 py-3'>
            {message.text && (
              <div className="text-gray-800 dark:text-white mb-4">
                {message.text}
              </div>
            )}
            {onMediaUpload ? (
              <MediaUpload
                onUpload={onMediaUpload}
                disabled={disabled}
                className="mt-3"
                primaryColor={primaryColor}
              />
            ) : (
              <div className="mt-2 text-sm tex-gray-900 dark:text-gray-100 p-4 bg-gray-50 rounded-lg">
                Please upload a file to continue.
              </div>
            )}
          </div>
        );

      case 'ask_address':
        return (
          <div>
            {message.text && (
              <div className="text-gray-800 dark:text-white p-3">
                {message.text}
              </div>
            )}
            <button
              onClick={() => setIsAddressModalOpen(true)}
              disabled={disabled}
              className="w-full p-3 border-t border-gray-300 dark:border-gray-600 hover:opacity-90 text-gray-700 dark:text-gray-300 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
             <MapPinHouse size={16} />
              <span className="text-sm font-medium">Provide Address</span>
            </button>
            {onAddressSubmit && (
              <AddressFormModal
                isOpen={isAddressModalOpen}
                onClose={() => setIsAddressModalOpen(false)}
                onSubmit={(address) => {
                  onAddressSubmit(address);
                  setIsAddressModalOpen(false);
                }}
                prompt={message.text}
                disabled={disabled}
                primaryColor={primaryColor}
                containedModal={containedModal}
              />
            )}
          </div>
        );

      case 'ask_location':
        return (
          <div>
            {message.text && (
              <div className="text-gray-800 dark:text-white p-3">
                {message.text}
              </div>
            )}
            {onLocationCapture && (
              <LocationCapture
                onLocationCapture={onLocationCapture}
                disabled={disabled}
              />
            )}
          </div>
        );

      case 'text':
      default:
        return (
          <div>
            {message.text && (
              <div className="text-gray-800 dark:text-white p-3">
                {message.text}
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className={`workflow-message ${className}`}>
      {renderContent()}
    </div>
  );
};

export default WorkflowMessage;
